import type { GraphQLTypes, InputType } from '@zeus/index'
import { $ } from '@zeus/index'
import { typedGql } from '@zeus/typedDocumentNode'
import { scalars } from '~/gql/utils/scalars'

/** L'état dont le lecteur repart : la tentative en cours, ses réponses, ses chapitres (#84, #158, #160). */
const resumeFields = {
  id: true,
  completionStatus: true,
  successStatus: true,
  totalTime: true,
  sessionTime: true,
  suspendData: true,
  location: true,
  completedAt: true,
  attemptNumber: true,
  // Dernier chapitre ouvert d'un module à plusieurs SCO (#160).
  currentScoIdentifier: true,
  currentAttempt: {
    number: true,
    completionStatus: true,
    successStatus: true,
    scoreRaw: true,
    scoreMin: true,
    scoreMax: true,
    scoreScaled: true,
    totalTime: true,
    // Réponses déjà enregistrées (#158) : le module les retrouve à la reprise.
    interactions: {
      scoIdentifier: true,
      index: true,
      interactionId: true,
      type: true,
      description: true,
      correctResponses: true,
      learnerResponse: true,
      result: true,
      weighting: true,
      latency: true,
      timestamp: true,
      objectiveIds: true,
    },
    // L'état de chaque chapitre déjà ouvert (#160), d'où chacun reprend.
    scoStates: {
      scoIdentifier: true,
      completionStatus: true,
      successStatus: true,
      scoreRaw: true,
      scoreMin: true,
      scoreMax: true,
      scoreScaled: true,
      totalTime: true,
      suspendData: true,
      location: true,
    },
  },
} as const

/**
 * Ouverture d'un module par le lecteur (#84) : reprend la tentative en cours,
 * ou démarre la suivante si elle est terminée. `currentAttempt` porte l'état
 * dont le paquet repart ; les champs de score, le score RETENU.
 */
export const GQL_START_SCORM_ATTEMPT = typedGql('mutation', { scalars })({
  startScormAttempt: [
    // `moduleId` cible le module du parcours (#139). Absent, l'API vise le premier module.
    { sessionId: $('sessionId', 'ID!'), moduleId: $('moduleId', 'ID') },
    resumeFields,
  ],
})

/**
 * La reprise telle qu'elle est maintenant, sans rien démarrer ni compter
 * d'ouverture : relue au changement de chapitre (#160), un chapitre joué dans
 * la séance ayant avancé depuis l'ouverture du module.
 */
export const GQL_MY_SCORM_RESUME = typedGql('query', { scalars })({
  myScormResume: [{ sessionId: $('sessionId', 'ID!'), moduleId: $('moduleId', 'ID!') }, resumeFields],
})

/** Les chapitres (SCO) d'un module, dans l'ordre (#160) ; vide pour un module à un seul SCO importé avant. */
export const GQL_MODULE_SCOS = typedGql('query', { scalars })({
  moduleScos: [{ moduleId: $('moduleId', 'ID!') }, { identifier: true, title: true, href: true, position: true }],
})

export const GQL_UPSERT_SCORM_TRACKING = typedGql('mutation')({
  upsertScormTracking: [
    {
      input: $('input', 'UpsertScormTrackingInput!'),
    },
    {
      id: true,
      completionStatus: true,
      successStatus: true,
      scoreRaw: true,
      scoreMin: true,
      scoreMax: true,
      scoreScaled: true,
      totalTime: true,
      suspendData: true,
      location: true,
      completedAt: true,
    },
  ],
})

// Agrégats du tableau de bord (#138). Requête distincte de la liste : elle porte
// sur tout le périmètre filtré, là où `getScormTrackings` ne rend qu'une page.
/** Courbe des complétions par jour (#12), sur une fenêtre glissante. */
export const GQL_SCORM_COMPLETIONS_BY_DAY = typedGql('query', { scalars })({
  getScormCompletionsByDay: [
    { input: $('input', 'ScormTrendInput!') },
    {
      day: true,
      completed: true,
    },
  ],
})

/**
 * Compteurs du tableau de bord (#138), lus aussi par la fiche session (#243) :
 * une seule sélection, pour que les deux écrans n'affichent pas des champs
 * différents du même calcul.
 */
export const trackingStatsFields = {
  total: true,
  learners: true,
  completed: true,
  inProgress: true,
  notStarted: true,
  completionUnknown: true,
  neverOpened: true,
  notYetUnlocked: true,
  passed: true,
  failed: true,
  averageScorePercent: true,
  scoredCount: true,
  averageTimeSeconds: true,
  timedCount: true,
  totalTimeSeconds: true,
} as const

export type TrackingStats = InputType<
  GraphQLTypes['ScormTrackingStats'],
  typeof trackingStatsFields,
  typeof scalars
>

export const GQL_SCORM_TRACKING_STATS = typedGql('query', { scalars })({
  getScormTrackingStats: [
    {
      input: $('input', 'ScormTrackingStatsInput!'),
    },
    trackingStatsFields,
  ],
})

export const GQL_SCORM_TRACKINGS = typedGql('query', { scalars })({
  getScormTrackings: [
    {
      input: $('input', 'FindManyScormTrackingWithFiltersInput!'),
    },
    {
      trackings: {
        id: true,
        completionStatus: true,
        successStatus: true,
        scoreRaw: true,
        scoreMin: true,
        scoreMax: true,
        scoreScaled: true,
        // Score corrigé par un admin (#161) ; absent = score calculé.
        correctedScore: true,
        totalTime: true,
        completedAt: true,
        userId: true,
        userEmail: true,
        userFirstName: true,
        userLastName: true,
        sessionId: true,
        sessionName: true,
      },
      total: true,
    },
  ],
})
